<template>
  <!--
    A la cistella i al pagament (2026-10-11): qui compra només el quadern d'exàmens o només els apunts definitius d'una
    assignatura que té pack, rep la proposta de canviar-ho pel pack (els dos, més barat). Si ja hi té els dos per separat,
    se li proposa el pack perquè li surt més barat. Canviar-ho passa per cart.js (galeta `cart` + avís 'cart-changed').
    Si hi té el pack i també el quadern o els apunts que ja inclou, l'avisa i els hi pot treure (2026-10-13).
  -->
  <div v-if="offers.length || duplicates.length" class="pack-upsell font-rubik">
    <!-- El pack i, a més, el quadern o els apunts que ja inclou: avís per no pagar-los dos cops -->
    <div v-for="d in duplicates" :key="'dup-' + d.pack.book_id" class="pu-card pu-card--warn">
      <img :src="d.pack.image" alt="" class="pu-img" loading="lazy"/>
      <div class="pu-body">
        <span class="pu-kicker">Ja ho tens al pack</span>
        <span class="pu-title">El pack {{ deApostrof(d.name) }} ja inclou {{ d.what }}: ara {{ d.both ? 'els' : 'el' }} pagaries dos cops.</span>
        <button type="button" class="pu-btn" @click="removeDuplicates(d)">
          Treu {{ d.short }} · −{{ euros(d.saving) }}
        </button>
      </div>
    </div>
    <div v-for="o in offers" :key="o.pack.book_id" class="pu-card">
      <img :src="o.pack.image" alt="" class="pu-img" loading="lazy"/>
      <div class="pu-body">
        <span class="pu-kicker">{{ o.both ? 'Et surt més barat en pack' : navigate ? 'Millor en pack' : 'Completa-ho amb el pack' }}</span>
        <span class="pu-title">{{ o.both ? `Pack exàmens + apunts ${deApostrof(o.name)}` : o.pitch }}</span>
        <span class="pu-price">
          <s v-if="o.pack.oldPrice">{{ euros(o.pack.oldPrice) }}</s>
          <b>{{ euros(o.pack.price) }}</b>
          <span class="pu-save">Estalvies {{ euros(o.pack.oldPrice - o.pack.price) }}</span>
        </span>
        <button v-if="navigate && packInCart(o)" type="button" class="pu-btn" @click="goToCart(o)">
          ✓ Pack afegit · Veure cistella
        </button>
        <button v-else type="button" class="pu-btn" :disabled="busy" @click="takePack(o)">
          {{ navigate ? `Vull el pack · ${euros(o.pack.price)}` : o.both ? 'Canvia-ho pel pack' : `Afegeix ${o.missing} · +${euros(o.pack.price - o.current.price)}` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {loadShopList, euros, deApostrof} from '../../../examens/widgets/dossier/dossierShop'
import {getCart, addPackToCart, removeFromCart, cartVersion} from '../cart'

export default {
  name: 'PackUpsell',
  props: {
    items: {type: Array, default: () => []},
    // A la fitxa del producte (BookPage): la proposta es queda a la pàgina i, un cop afegit el pack, el botó porta a
    // la cistella (abans hi anava directament, però sovint en compren de més d'una assignatura)
    navigate: {type: Boolean, default: false}
  },
  data() {
    return {all: [], busy: false}
  },
  computed: {
    offers() {
      if (!this.all.length) return []
      const inCart = new Set(this.items.map(i => i.book_id))
      const byId = id => this.all.find(b => b.book_id === id)
      const out = []
      for (const notes of this.all.filter(b => b.kind === 'notes' && b.packBookId && b.examBookId)) {
        const pack = byId(notes.packBookId)
        const exam = byId(notes.examBookId)
        if (!pack || !exam || inCart.has(pack.book_id)) continue
        const hasExam = inCart.has(exam.book_id), hasNotes = inCart.has(notes.book_id)
        if (!hasExam && !hasNotes) continue
        const name = notes.new_subject_name || notes.subject_name
        const parts = [exam.book_id, notes.book_id]
        if (hasExam && hasNotes) {
          out.push({pack, name, parts, both: true, current: {price: exam.price + notes.price}})
        } else if (hasExam) {
          out.push({pack, name, parts, current: exam, missing: 'els apunts',
            pitch: `Afegeix-hi els apunts definitius ${deApostrof(name)}: tot el temari amb esquemes i preguntes de repàs`})
        } else {
          out.push({pack, name, parts, current: notes, missing: "el quadern d'exàmens",
            pitch: `Afegeix-hi el quadern d'exàmens ${deApostrof(name)}: els últims exàmens per temes amb la solució al costat`})
        }
      }
      return out
    },
    // Packs de la cistella amb el quadern i/o els apunts que ja inclouen també a la cistella (a la fitxa no)
    duplicates() {
      if (this.navigate || !this.all.length) return []
      const byId = id => this.all.find(b => b.book_id === id)
      const out = []
      for (const item of this.items) {
        const pack = byId(item.book_id)
        if (!pack || pack.kind !== 'pack') continue
        const parts = this.items.filter(i => i.book_id === pack.examBookId || i.book_id === pack.notesBookId)
        if (!parts.length) continue
        const hasExam = parts.some(i => i.book_id === pack.examBookId), hasNotes = parts.some(i => i.book_id === pack.notesBookId)
        const what = hasExam && hasNotes ? "el quadern d'exàmens i els apunts" : hasExam ? "el quadern d'exàmens" : 'els apunts'
        const short = hasExam && hasNotes ? 'tots dos' : hasExam ? 'el quadern' : 'els apunts'
        out.push({pack, parts: parts.map(i => i.book_id), what, short, both: hasNotes, name: pack.new_subject_name || pack.subject_name,
          saving: parts.reduce((s, i) => s + Number(i.price), 0)})
      }
      return out
    }
  },
  watch: {
    offers(list) {
      for (const o of list) this.$funnel.trackOncePerDay('ad_impression', {placement: 'pack_upsell', book_id: o.pack.book_id, subject_id: o.pack.subject_id, meta: {both: !!o.both}})
    }
  },
  async created() {
    try {
      const list = await loadShopList()
      this.all = (list || []).flatMap(b => [...(b.subjects || []), ...(b.apunts || [])])
    } catch (e) {
      this.all = []
    }
  },
  methods: {
    euros,
    deApostrof,
    packInCart(o) {
      cartVersion.n
      return this.$root.cart > 0 && Number(getCart(this)[o.pack.book_id]) > 0
    },
    goToCart(o) {
      this.$funnel.adClick('pack_upsell', {book_id: o.pack.book_id, subject_id: o.pack.subject_id, meta: {target: 'checkout'}})
      this.$router.push({path: '/checkout'})
    },
    removeDuplicates(d) {
      for (const id of d.parts) removeFromCart(this, id)
      this.$funnel.adClick('pack_duplicate', {book_id: d.pack.book_id, subject_id: d.pack.subject_id, meta: {target: 'remove', parts: d.parts}})
    },
    takePack(o) {
      this.busy = true
      // El pack substitueix una unitat del quadern i/o dels apunts que ja hi hagi a la cistella (també des de la
      // fitxa del producte: si ja hi tenien el quadern i hi afegien el pack, el pagaven dos cops)
      addPackToCart(this, o.pack, o.parts)
      this.$funnel.adClick('pack_upsell', {book_id: o.pack.book_id, subject_id: o.pack.subject_id, meta: {target: 'add_to_cart', both: !!o.both}})
      this.$funnel.track('add_to_cart', {placement: this.navigate ? 'pack_upsell_book' : 'pack_upsell', book_id: o.pack.book_id, subject_id: o.pack.subject_id})
      this.busy = false
    }
  }
}
</script>

<style scoped>
.pack-upsell {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 4px 0 18px;
}
.pu-card {
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 2px dashed #9FD8BD;
  border-radius: 14px;
  background: #F2FBF6;
}
.pu-card--warn {
  border-color: #F5C48A;
  background: #FFF7EC;
}
.pu-card--warn .pu-kicker {
  color: #B45309;
}
.pu-card--warn .pu-btn {
  background: #D97706;
  box-shadow: 0 3px 0 #A55A04;
}
.pu-img {
  flex: 0 0 92px;
  width: 92px;
  height: 64px;
  object-fit: cover;
  border-radius: 10px;
  background: #EFEFEF;
}
.pu-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pu-kicker {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1B8F5D;
}
.pu-title {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  color: rgb(46, 56, 86);
}
.pu-price {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
}
.pu-price s {
  color: #9ca3af;
}
.pu-save {
  font-size: 12px;
  font-weight: 700;
  color: #1B8F5D;
}
.pu-btn {
  align-self: flex-start;
  margin-top: 4px;
  padding: 8px 14px;
  border: none;
  border-radius: 10px;
  background: #1B8F5D;
  box-shadow: 0 3px 0 #136943;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.pu-btn:disabled {
  opacity: 0.6;
}
.pu-btn:active {
  transform: translateY(3px);
  box-shadow: none;
}
</style>
